/**
 * Admin Console Types
 * Implements Requirement 82: Web UI — Admin Console [MVP]
 */

import { UserRole } from './auth';

/**
 * Cloud provider types for onboarding
 */
export enum CloudProvider {
  AWS = 'aws',
  AZURE = 'azure',
  GCP = 'gcp',
}

/**
 * Cloud account onboarding status
 */
export enum OnboardingStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
  // Set on every account created through the real onboarding form - the
  // platform hasn't actually verified the role/service-principal/WIF config
  // works yet (that needs live cloud credentials to test against, see
  // Requirement 119 AC2), so this is honestly distinct from COMPLETED.
  PENDING_VERIFICATION = 'pending_verification',
}

/**
 * AWS IAM role configuration
 */
export interface AWSIAMRoleConfig {
  accountId: string;
  roleName: string;
  // Collected but not referenced by the generated AWS trust policy - the
  // platform federates via Entra ID OIDC, not sts:AssumeRole + sts:ExternalId.
  externalId: string;
  curBucketName: string;
  curBucketRegion: string;
  curPrefix?: string;
  // Adds organizations:ListAccounts to the generated role so "Find Siblings"
  // works without a manual policy edit after deploying the template.
  isOrganizationManagementAccount?: boolean;
  // Adds CloudFormation StackSets permissions so the platform can deploy the
  // member-account role itself. The only grant here that lets the platform
  // create resources rather than read billing data - opt-in, off by default.
  allowPlatformManagedStackSets?: boolean;
}

/**
 * Azure service principal configuration
 */
export interface AzureServicePrincipalConfig {
  tenantId: string;
  subscriptionId: string;
  clientId: string;
  clientSecret: string;
  storageAccountName: string;
  containerName: string;
}

/**
 * GCP Workload Identity Federation configuration
 */
export interface GCPWIFConfig {
  projectId: string;
  workloadIdentityPoolId: string;
  workloadIdentityProviderId: string;
  serviceAccountEmail: string;
  billingExportDataset: string;
  billingExportTable: string;
}

/**
 * Cloud account configuration
 */
export interface CloudAccount {
  id: string;
  provider: CloudProvider;
  accountName: string;
  accountId: string;
  status: OnboardingStatus;
  config: AWSIAMRoleConfig | AzureServicePrincipalConfig | GCPWIFConfig;
  hasStoredSecret?: boolean;
  lastVerifiedAt?: string | null;
  verificationError?: string | null;
  /**
   * Permissions the onboarding preflight found missing. Advisory only — the
   * account still onboards and its cost data still works; what these gate is
   * Azure metrics and ARM cost queries. Cleared automatically by Verify Now
   * once the grants are in place.
   */
  preflightWarnings?: string[] | null;
  // Requirement 119 AC4: one-time historical-backfill scan status, fired
  // automatically right after the first successful verification.
  backfillStatus?: 'not_started' | 'in_progress' | 'completed' | 'failed';
  backfillEarliestDate?: string | null;
  backfillRowCount?: number | null;
  // Multi-account onboarding fix: null for pre-existing rows and normal
  // standalone accounts - only set once a real master/sibling relationship
  // is known (see CloudOnboardingTab.tsx's sibling-onboarding flow).
  accountType?: 'master' | 'member' | 'standalone' | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Ingestion health status
 */
export enum IngestionHealthStatus {
  HEALTHY = 'healthy',
  WARNING = 'warning',
  ERROR = 'error',
  UNKNOWN = 'unknown',
}

/**
 * Ingestion health details - real, per-account (Requirement 172 AC6).
 * recordsProcessed/recordsFailed and nextSyncTime were dropped: this
 * platform queries cost data live from each provider on demand rather than
 * running a batch ingestion pipeline, so there's no real "sync job" or
 * record count to report honestly.
 */
export interface IngestionHealth {
  id: string; // internal cloud_accounts UUID - needed for the Verify Now action
  accountId: string;
  accountName: string;
  provider: CloudProvider;
  status: IngestionHealthStatus;
  lastSyncTime: string | null;
  errorDetails: string | null;
  dataFreshness: number | null; // hours since the freshest data point seen, or null if never verified
  completenessCount: number | null; // row/blob count from the latest health check, for trend context
  correctnessOk: boolean | null; // schema/shape check on the latest fetched data, null if never checked
  correctnessDetail: string | null;
  driftClassification: 'customer_revoked_access' | 'platform_bug' | 'transient' | 'unknown' | null; // only populated when status is error
}

/**
 * User management
 */
export interface TenantUser {
  id: string;
  email: string;
  name: string;
  roles: UserRole[];
  status: 'active' | 'inactive' | 'pending';
  lastLogin: string | null;
  createdAt: string;
  /**
   * Keycloak actions the user must complete interactively (CONFIGURE_TOTP,
   * UPDATE_PASSWORD, VERIFY_EMAIL). Because sign-in uses the Direct Access
   * Grant (ROPC), which has no interactive step, ANY pending action blocks
   * login outright — and Keycloak reports it as the same generic error as a
   * wrong password, so it has to be surfaced explicitly.
   */
  requiredActions?: string[];
  /** Keycloak brute-force lockout. Tracked separately from `status`, and not cleared by re-enabling. */
  bruteForceLocked?: boolean;
  /** True when this account cannot sign in regardless of the password supplied. */
  loginBlocked?: boolean;
}

/**
 * Integration types
 */
export enum IntegrationType {
  SLACK = 'slack',
  TEAMS = 'teams',
  JIRA = 'jira',
  SERVICENOW = 'servicenow',
}

/**
 * Integration configuration
 */
export interface Integration {
  id: string;
  type: IntegrationType;
  name: string;
  enabled: boolean;
  config: Record<string, unknown>;
  status: 'connected' | 'disconnected' | 'error';
  lastSync: string | null;
  createdAt: string;
}

/**
 * AI tier types
 */
export enum AITier {
  T_ML = 't_ml',
  T1 = 't1',
  T2 = 't2',
  T3 = 't3',
}

/**
 * AI feature types
 */
export enum AIFeature {
  ANOMALY_EXPLANATION = 'anomaly_explanation',
  RCA_HYPOTHESIS = 'rca_hypothesis',
  RECOMMENDATION_EXPLANATION = 'recommendation_explanation',
  OWNERSHIP_HYPOTHESIS = 'ownership_hypothesis',
  COPILOT = 'copilot',
  NL_COST_EXPLORER = 'nl_cost_explorer',
}

/**
 * AI tier configuration
 */
export interface AITierConfig {
  tier: AITier;
  enabled: boolean;
  features: AIFeature[];
  costCap: number | null; // USD per month
  modelPreference: string | null;
}

/**
 * AI configuration
 */
export interface AIConfig {
  killSwitchEnabled: boolean;
  tiers: AITierConfig[];
  perFeatureConsent: Record<AIFeature, boolean>;
  totalMonthlyCostCap: number | null;
  currentMonthlySpend: number;
}

/**
 * Feature flag status
 */
export interface FeatureFlag {
  id: string;
  flagKey: string;
  name: string;
  description: string;
  enabled: boolean;
}

/**
 * Deployment mode
 */
export enum DeploymentMode {
  HOSTED_SAAS = 'hosted_saas',
  CUSTOMER_PREMISES_ONLINE = 'customer_premises_online',
  CUSTOMER_PREMISES_OFFLINE = 'customer_premises_offline',
}

/**
 * Admin console tab types
 */
export enum AdminTab {
  CLOUD_ONBOARDING = 'cloud_onboarding',
  USER_MANAGEMENT = 'user_management',
  INTEGRATIONS = 'integrations',
  INGESTION_HEALTH = 'ingestion_health',
  AI_CONFIG = 'ai_config',
  FEATURE_FLAGS = 'feature_flags',
  BUDGET_SETTINGS = 'budget_settings',
  CHANGE_WINDOWS = 'change_windows',
  ANOMALY_MODEL_HEALTH = 'anomaly_model_health',
  PLATFORM_ANOMALIES = 'platform_anomalies',
  DEMO_SCENARIOS = 'demo_scenarios',
}
